import { useEffect, useMemo, useRef, useState } from 'react';
import {
  consultarEstadoMotor,
  executarItemBenchessmark,
  listarCasosBenchessmark,
  listarModelosPublicos
} from '../lib/api';
import type {
  CasoBenchessmark,
  EstadoMotorPublico,
  ModeloPublico,
  ModoAssistenciaModelo,
  ResultadoItemBenchessmark
} from '../types';
import { Icone } from './Icone';
import { Peca } from './Peca';

interface Props {
  aoVoltar: () => void;
  aoAdministrar: () => void;
}

interface FalhaItem {
  modeloPublicoId: string;
  casoId: string;
  mensagem: string;
}

interface ResumoModelo {
  modelo: ModeloPublico;
  quantidade: number;
  nota: number;
  perdaMedia: number;
  legalidade: number;
  primeiraResposta: number;
  top1: number;
  top3: number;
  latencia: number;
  alternativas: number;
}

const modos: Array<{ id: ModoAssistenciaModelo; nome: string; rotulo: string; descricao: string }> = [
  {
    id: 'puro',
    nome: 'Modelo puro',
    rotulo: 'Mede inteligência bruta',
    descricao: 'O modelo recebe a posição e todas as jogadas legais, sem notas nem filtro do Stockfish.'
  },
  {
    id: 'orientado',
    nome: 'Orientado',
    rotulo: 'Mede decisão assistida',
    descricao: 'O Stockfish apresenta dez candidatas fortes; o modelo escolhe a continuação.'
  },
  {
    id: 'regio',
    nome: 'Precisão Régia',
    rotulo: 'Mede força durante o jogo',
    descricao: 'O Stockfish reduz o campo às melhores candidatas para evitar decisões ruins.'
  }
];

const dificuldadeNome: Record<CasoBenchessmark['dificuldade'], string> = {
  essencial: 'Essencial',
  avancado: 'Avançado',
  especialista: 'Especialista'
};

function pecasDoFen(fen: string): Array<{ casa: number; tipo: 'p'|'n'|'b'|'r'|'q'|'k'; cor: 'w'|'b' }> {
  const tabuleiro = fen.split(' ')[0];
  const saida: Array<{ casa: number; tipo: 'p'|'n'|'b'|'r'|'q'|'k'; cor: 'w'|'b' }> = [];
  let casa = 0;
  for (const caractere of tabuleiro) {
    if (caractere === '/') continue;
    if (/\d/.test(caractere)) { casa += Number(caractere); continue; }
    const minusculo = caractere.toLowerCase() as 'p'|'n'|'b'|'r'|'q'|'k';
    saida.push({ casa, tipo: minusculo, cor: caractere === minusculo ? 'b' : 'w' });
    casa += 1;
  }
  return saida;
}

function MiniTabuleiro({ fen }: { fen: string }) {
  const pecas = pecasDoFen(fen);
  const mapa = new Map(pecas.map(peca => [peca.casa, peca]));
  return (
    <div className="mini-tabuleiro-bench" aria-label="Diagrama da posição">
      {Array.from({ length: 64 }, (_, indice) => {
        const peca = mapa.get(indice);
        const linha = Math.floor(indice / 8);
        const coluna = indice % 8;
        return <span key={indice} className={(linha + coluna) % 2 ? 'mini-casa-escura' : 'mini-casa-clara'}>{peca && <Peca tipo={peca.tipo} cor={peca.cor}/>}</span>;
      })}
    </div>
  );
}

function origemMotor(origem: EstadoMotorPublico['origem']): string {
  if (origem === 'nativo') return 'Executável nativo';
  if (origem === 'wasm-completo') return 'Versão completa com rede neural';
  return 'Versão de compatibilidade';
}

function percentual(valor: number): string {
  return `${Math.round(valor * 100)}%`;
}

function resumoPorModelo(modelos: ModeloPublico[], resultados: ResultadoItemBenchessmark[]): ResumoModelo[] {
  return modelos.map(modelo => {
    const itens = resultados.filter(resultado => resultado.modeloPublicoId === modelo.idPublico);
    const quantidade = itens.length;
    const divisor = Math.max(1, quantidade);
    return {
      modelo,
      quantidade,
      nota: Math.round(itens.reduce((soma, item) => soma + item.nota, 0) / divisor),
      perdaMedia: Math.round(itens.reduce((soma, item) => soma + item.perdaCentipoes, 0) / divisor),
      legalidade: itens.filter(item => item.legal).length / divisor,
      primeiraResposta: itens.filter(item => item.legal && item.tentativas === 1).length / divisor,
      top1: itens.filter(item => item.top1).length / divisor,
      top3: itens.filter(item => item.top3).length / divisor,
      latencia: Math.round(itens.reduce((soma, item) => soma + item.latenciaMs, 0) / divisor),
      alternativas: itens.filter(item => item.usouAlternativa).length
    };
  }).filter(resumo => resumo.quantidade > 0).sort((a, b) => b.nota - a.nota || a.perdaMedia - b.perdaMedia);
}

function baixarArquivo(nome: string, conteudo: string, tipo: string) {
  const url = URL.createObjectURL(new Blob([conteudo], { type: tipo }));
  const ancora = document.createElement('a');
  ancora.href = url;
  ancora.download = nome;
  ancora.click();
  URL.revokeObjectURL(url);
}

export function Benchessmark({ aoVoltar, aoAdministrar }: Props) {
  const [casos, setCasos] = useState<CasoBenchessmark[]>([]);
  const [modelos, setModelos] = useState<ModeloPublico[]>([]);
  const [selecionados, setSelecionados] = useState<string[]>([]);
  const [modo, setModo] = useState<ModoAssistenciaModelo>('puro');
  const [suite, setSuite] = useState<'rapida'|'completa'>('rapida');
  const [resultados, setResultados] = useState<ResultadoItemBenchessmark[]>([]);
  const [falhas, setFalhas] = useState<FalhaItem[]>([]);
  const [executando, setExecutando] = useState(false);
  const [progresso, setProgresso] = useState({ atual: 0, total: 0, modelo: '', caso: '' });
  const [mensagem, setMensagem] = useState<string | null>(null);
  const [estadoMotor, setEstadoMotor] = useState<EstadoMotorPublico | null>(null);
  const [casoAberto, setCasoAberto] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    void Promise.all([listarCasosBenchessmark(), listarModelosPublicos(), consultarEstadoMotor()])
      .then(([listaCasos, listaModelos, motor]) => {
        setCasos(listaCasos);
        setModelos(listaModelos);
        setSelecionados(listaModelos.slice(0, 2).map(modelo => modelo.idPublico));
        setEstadoMotor(motor);
      })
      .catch(erro => setMensagem(erro instanceof Error ? erro.message : 'Não foi possível preparar o Benchessmark.'));
    return () => abortRef.current?.abort();
  }, []);

  const casosEscolhidos = useMemo(() => suite === 'rapida'
    ? casos.filter(caso => ['mate-escolar', 'ruy-lopez', 'gambito-dama', 'figado-frito', 'rei-e-peao', 'meio-jogo-complexo'].includes(caso.id))
    : casos, [casos, suite]);
  const modelosEscolhidos = useMemo(() => modelos.filter(modelo => selecionados.includes(modelo.idPublico)), [modelos, selecionados]);
  const resumos = useMemo(() => resumoPorModelo(modelos, resultados), [modelos, resultados]);
  const totalPrevisto = casosEscolhidos.length * modelosEscolhidos.length;

  function alternarModelo(id: string) {
    setSelecionados(atuais => atuais.includes(id) ? atuais.filter(item => item !== id) : [...atuais, id]);
  }

  async function executar() {
    if (!modelosEscolhidos.length || !casosEscolhidos.length || executando) return;
    const controle = new AbortController();
    abortRef.current = controle;
    setExecutando(true);
    setResultados([]);
    setFalhas([]);
    setMensagem(null);
    const total = modelosEscolhidos.length * casosEscolhidos.length;
    let atual = 0;
    const novosResultados: ResultadoItemBenchessmark[] = [];
    const novasFalhas: FalhaItem[] = [];
    try {
      for (const modelo of modelosEscolhidos) {
        for (const caso of casosEscolhidos) {
          if (controle.signal.aborted) throw new DOMException('Cancelado', 'AbortError');
          atual += 1;
          setProgresso({ atual, total, modelo: modelo.nome, caso: caso.titulo });
          try {
            const resultado = await executarItemBenchessmark({ modeloPublicoId: modelo.idPublico, casoId: caso.id, modo }, controle.signal);
            novosResultados.push(resultado);
            setResultados([...novosResultados]);
          } catch (erro) {
            if (controle.signal.aborted) throw erro;
            novasFalhas.push({ modeloPublicoId: modelo.idPublico, casoId: caso.id, mensagem: erro instanceof Error ? erro.message : 'O item não pôde ser executado.' });
            setFalhas([...novasFalhas]);
          }
        }
      }
      setMensagem(`Benchessmark concluído: ${novosResultados.length} item(ns) medido(s)${novasFalhas.length ? ` e ${novasFalhas.length} falha(s)` : ''}.`);
    } catch (erro) {
      if (erro instanceof DOMException && erro.name === 'AbortError') setMensagem('Execução interrompida. Os resultados já concluídos foram preservados.');
      else setMensagem(erro instanceof Error ? erro.message : 'A execução foi interrompida.');
    } finally {
      setExecutando(false);
      abortRef.current = null;
    }
  }

  function cancelar() {
    abortRef.current?.abort();
  }

  function exportarJson() {
    const dados = {
      nome: 'Benchessmark Xadrez',
      geradoEm: new Date().toISOString(),
      modo,
      suite,
      motor: estadoMotor,
      modelos: modelosEscolhidos,
      casos: casosEscolhidos,
      resultados,
      falhas,
      resumo: resumos
    };
    baixarArquivo(`benchessmark-${new Date().toISOString().slice(0, 10)}.json`, JSON.stringify(dados, null, 2), 'application/json;charset=utf-8');
  }

  function exportarCsv() {
    const cabecalho = ['modelo', 'provedor', 'caso', 'modo', 'nota', 'perda_centipoes', 'legal', 'top1', 'top3', 'tentativas', 'alternativa', 'latencia_ms', 'jogada_modelo', 'melhor_jogada'];
    const linhas = resultados.map(resultado => {
      const modelo = modelos.find(item => item.idPublico === resultado.modeloPublicoId);
      const caso = casos.find(item => item.id === resultado.casoId);
      return [modelo?.nome, modelo?.provedor, caso?.titulo, resultado.modo, resultado.nota, resultado.perdaCentipoes, resultado.legal, resultado.top1, resultado.top3, resultado.tentativas, resultado.usouAlternativa, resultado.latenciaMs, resultado.jogadaModelo ?? '', resultado.melhorJogada]
        .map(valor => `"${String(valor ?? '').replaceAll('"', '""')}"`).join(',');
    });
    baixarArquivo(`benchessmark-${new Date().toISOString().slice(0, 10)}.csv`, [cabecalho.join(','), ...linhas].join('\n'), 'text/csv;charset=utf-8');
  }

  return (
    <main className="tela-benchessmark">
      <header className="cabecalho-bench">
        <button type="button" className="botao-voltar" onClick={aoVoltar}><Icone nome="voltar"/> Voltar ao salão</button>
        <div className="marca-bench"><span><Icone nome="xadrez" tamanho={24}/></span><div><strong>BENCHESSMARK</strong><small>LABORATÓRIO DE MODELOS</small></div></div>
        <button type="button" className="botao-texto-discreto" onClick={aoAdministrar}>Configurar modelos</button>
      </header>

      <section className="abertura-bench">
        <div>
          <p className="sobretitulo"><span/> MEDIÇÃO REPRODUZÍVEL</p>
          <h1>Qual modelo<br/><em>realmente joga melhor?</em></h1>
          <p>Uma suíte de posições táticas, estratégicas e finais. Cada escolha é validada, comparada com o Stockfish 18 e convertida em métricas que podem ser exportadas.</p>
        </div>
        <aside className="placa-referencia-bench">
          <span className="medalhao-bench"><Icone nome="coroa" tamanho={30}/></span>
          <div><small>MOTOR DE REFERÊNCIA</small><strong>{estadoMotor?.nome ?? 'Stockfish 18'}</strong><p>{estadoMotor?.disponivel ? `${origemMotor(estadoMotor.origem)} · ${estadoMotor.threads} processo(s) · ${estadoMotor.hashMb} MB` : 'Aguardando confirmação do motor.'}</p></div>
        </aside>
      </section>

      <section className="painel-configuracao-bench">
        <div className="bloco-config-bench">
          <header><span>01</span><div><strong>Modelos em comparação</strong><small>Selecione um ou mais modelos liberados</small></div></header>
          {modelos.length ? <div className="lista-modelos-bench">{modelos.map(modelo => (
            <label key={modelo.idPublico} className={selecionados.includes(modelo.idPublico) ? 'modelo-bench-selecionado' : ''}>
              <input type="checkbox" checked={selecionados.includes(modelo.idPublico)} disabled={executando} onChange={() => alternarModelo(modelo.idPublico)}/>
              <i/>
              <span><strong>{modelo.nome}</strong><small>{modelo.provedor}</small></span>
            </label>
          ))}</div> : <div className="vazio-bench"><Icone nome="modelo" tamanho={30}/><strong>Nenhum modelo liberado</strong><p>Configure um provedor e libere ao menos um modelo para executar a comparação.</p><button type="button" className="botao-contorno" onClick={aoAdministrar}>Abrir administração</button></div>}
        </div>

        <div className="bloco-config-bench">
          <header><span>02</span><div><strong>Grau de assistência</strong><small>O mesmo modo será aplicado a todos</small></div></header>
          <div className="modos-bench">{modos.map(item => (
            <button key={item.id} type="button" disabled={executando} className={modo === item.id ? 'modo-bench-ativo' : ''} onClick={() => setModo(item.id)}>
              <span><strong>{item.nome}</strong><em>{item.rotulo}</em></span><small>{item.descricao}</small>
            </button>
          ))}</div>
        </div>

        <div className="bloco-config-bench bloco-suite-bench">
          <header><span>03</span><div><strong>Extensão da suíte</strong><small>Chamadas são realizadas uma por vez</small></div></header>
          <div className="seletor-suite-bench">
            <button type="button" disabled={executando} className={suite === 'rapida' ? 'suite-ativa' : ''} onClick={() => setSuite('rapida')}><strong>Rápida</strong><small>6 posições por modelo</small></button>
            <button type="button" disabled={executando} className={suite === 'completa' ? 'suite-ativa' : ''} onClick={() => setSuite('completa')}><strong>Completa</strong><small>18 posições por modelo</small></button>
          </div>
          <div className="estimativa-bench"><Icone nome="relogio" tamanho={18}/><span><strong>{totalPrevisto} chamada(s) de modelo</strong><small>Além das análises locais do Stockfish. A execução pode consumir créditos dos provedores.</small></span></div>
        </div>
      </section>

      <section className="acao-bench">
        <div><small>PROTOCOLO ESCOLHIDO</small><strong>{modos.find(item => item.id === modo)?.nome}</strong><span>{casosEscolhidos.length} posições × {modelosEscolhidos.length} modelo(s)</span></div>
        {executando ? <button type="button" className="botao-parar-bench" onClick={cancelar}><Icone nome="fechar"/> Interromper medição</button> : <button type="button" className="botao-iniciar botao-executar-bench" disabled={!totalPrevisto} onClick={() => void executar()}><Icone nome="teste"/><span>Executar Benchessmark</span></button>}
      </section>

      {(executando || progresso.total > 0) && (
        <section className="progresso-bench" aria-live="polite">
          <div className="trilho-progresso-bench"><i style={{ width: `${progresso.total ? (progresso.atual / progresso.total) * 100 : 0}%` }}/></div>
          <div><strong>{executando ? 'Medição em andamento' : 'Última medição'}</strong><span>{progresso.atual} de {progresso.total}</span></div>
          <p>{executando ? `${progresso.modelo} · ${progresso.caso}` : mensagem}</p>
        </section>
      )}

      {mensagem && !executando && <div className="mensagem-bench" role="status"><Icone nome="escudo" tamanho={19}/><span>{mensagem}</span><button type="button" onClick={() => setMensagem(null)} aria-label="Fechar mensagem"><Icone nome="fechar" tamanho={15}/></button></div>}

      {resumos.length > 0 && (
        <section className="resultados-bench">
          <header className="topo-resultados-bench"><div><span>PLACAR GERAL</span><h2>Classificação dos modelos</h2><p>A nota combina qualidade da jogada e confiabilidade do formato. Menor perda média é melhor.</p></div><div><button type="button" className="botao-contorno" onClick={exportarCsv}><Icone nome="baixar" tamanho={17}/> Exportar planilha</button><button type="button" className="botao-contorno" onClick={exportarJson}><Icone nome="baixar" tamanho={17}/> Exportar dados</button></div></header>
          <div className="placar-bench">
            {resumos.map((resumo, indice) => (
              <article key={resumo.modelo.idPublico} className={indice === 0 ? 'campeao-bench' : ''}>
                <span className="posicao-bench">{String(indice + 1).padStart(2, '0')}</span>
                <div className="identidade-modelo-bench"><small>{resumo.modelo.provedor}</small><strong>{resumo.modelo.nome}</strong><span>{resumo.quantidade} posição(ões) concluída(s)</span></div>
                <div className="nota-bench"><small>NOTA</small><strong>{resumo.nota}</strong><span>/ 100</span></div>
                <dl>
                  <div><dt>Perda média</dt><dd>{resumo.perdaMedia} cp</dd></div>
                  <div><dt>Jogadas válidas</dt><dd>{percentual(resumo.legalidade)}</dd></div>
                  <div><dt>Válida de primeira</dt><dd>{percentual(resumo.primeiraResposta)}</dd></div>
                  <div><dt>Melhor escolha</dt><dd>{percentual(resumo.top1)}</dd></div>
                  <div><dt>Entre as três melhores</dt><dd>{percentual(resumo.top3)}</dd></div>
                  <div><dt>Tempo médio</dt><dd>{(resumo.latencia / 1000).toFixed(1)} s</dd></div>
                  <div><dt>Alternativas usadas</dt><dd>{resumo.alternativas}</dd></div>
                </dl>
              </article>
            ))}
          </div>
        </section>
      )}

      <section className="casos-bench">
        <header><div><span>SUÍTE DE POSIÇÕES</span><h2>{casosEscolhidos.length} provas de decisão</h2></div><p>As posições permanecem iguais entre execuções para permitir comparação. O motor de referência analisa cada jogada com múltiplas variantes.</p></header>
        <div className="grade-casos-bench">{casosEscolhidos.map((caso, indice) => {
          const resultadosCaso = resultados.filter(resultado => resultado.casoId === caso.id);
          const aberto = casoAberto === caso.id;
          return (
            <article key={caso.id} className={aberto ? 'caso-bench-aberto' : ''}>
              <button type="button" className="cabecalho-caso-bench" onClick={() => setCasoAberto(aberto ? null : caso.id)}>
                <span className="numero-caso-bench">{String(indice + 1).padStart(2, '0')}</span>
                <div><small>{caso.tema} · {dificuldadeNome[caso.dificuldade]}</small><strong>{caso.titulo}</strong><p>{caso.descricao}</p></div>
                <span className="indicador-caso-bench">{resultadosCaso.length ? `${resultadosCaso.length} resultado(s)` : 'Ver posição'} <Icone nome="avancar" tamanho={16}/></span>
              </button>
              {aberto && <div className="corpo-caso-bench"><MiniTabuleiro fen={caso.fen}/><div><label>FEN da posição</label><code>{caso.fen}</code>{resultadosCaso.length ? <div className="resultados-caso-bench">{resultadosCaso.map(resultado => {
                const modelo = modelos.find(item => item.idPublico === resultado.modeloPublicoId);
                return <div key={`${resultado.modeloPublicoId}-${resultado.casoId}`}><span><strong>{modelo?.nome}</strong><small>{resultado.jogadaModelo ?? 'Sem jogada válida'} · melhor: {resultado.melhorJogada}</small></span><b>{resultado.nota}</b></div>;
              })}</div> : <p>Esta posição ainda não foi medida na execução atual.</p>}</div></div>}
            </article>
          );
        })}</div>
      </section>

      {falhas.length > 0 && <section className="falhas-bench"><h2>Itens não concluídos</h2>{falhas.map((falha, indice) => {
        const modelo = modelos.find(item => item.idPublico === falha.modeloPublicoId);
        const caso = casos.find(item => item.id === falha.casoId);
        return <div key={`${falha.modeloPublicoId}-${falha.casoId}-${indice}`}><strong>{modelo?.nome} · {caso?.titulo}</strong><span>{falha.mensagem}</span></div>;
      })}</section>}
    </main>
  );
}
